import { Role } from "../../core/store/profile/profile.model";

/**
 * The role lists the router and the sidenav both draw from.
 *
 * They used to be written out separately in `app.routes.ts` and
 * `toolbar.component.ts`, and drifted: the sidenav hid nine pages from a
 * COMPLIANCE operator that the router happily served to anyone who typed the
 * URL. Hiding a link is not access control, so the two now share these lists —
 * change a role's reach in one place and both follow.
 *
 * `MANAGER` accompanies `ADMIN` everywhere: it is the pre-rename name for the
 * same role, kept until sessions minted before the rename expire. See
 * {@link Role}.
 */

/** Everyone who can hold an admin session, COMPLIANCE included. */
export const ROLES_ALL: Role[] = [
    "USER",
    "ADMIN",
    "MANAGER",
    "COMPLIANCE",
    "INTEGRATION",
    "SUPER_ADMIN",
];

/**
 * Everyone except COMPLIANCE.
 *
 * A compliance operator's API surface is incidents and preinvestigation reports
 * — roughly ten of the admin endpoints. Every other page 403s on its first
 * request, so it is gated rather than left to fail halfway through loading.
 */
export const ROLES_EXCEPT_COMPLIANCE: Role[] = ROLES_ALL.filter(
    (role) => role !== "COMPLIANCE",
);

/**
 * Roles that may act on a case's money and its audit trail: invoices, logs and
 * the settings page. Excludes `USER`, which is read-only.
 */
export const ROLES_CASE_PRIVILEGED: Role[] = [
    "ADMIN",
    "MANAGER",
    "INTEGRATION",
    "SUPER_ADMIN",
];

/** Settings — the admin-settings endpoints behind it are `hasRole('ADMIN')`. */
export const ROLES_SETTINGS: Role[] = ["ADMIN", "MANAGER", "SUPER_ADMIN"];

/** Operator management. Mirrors the server exactly; do not widen. */
export const ROLES_OPERATORS: Role[] = ["SUPER_ADMIN"];

/**
 * Whether a role belongs to one of these lists.
 *
 * The store holds `Profile.role` as a plain `string` — it arrives from the
 * server — so callers would otherwise have to cast at every check. Takes the
 * loose type and answers `false` for a missing or unrecognised role.
 */
export function hasRole(roles: readonly Role[], role: string | undefined | null): boolean {
    return !!role && (roles as readonly string[]).includes(role);
}
